/**
 * CondensedThreadView — view "thread" inline do popover condensado
 * (Mensageria Condensada · superfície 2 · PR F1a).
 *
 * Ler + responder SEM navegar (P13). Subheader com voltar + contraparte + star
 * (fixar/desafixar via `usePinMutation`) → timeline com MARCADORES DE ÂNCORA POR
 * TRECHO (porta para o peek, F1b) → composer inline (Enter envia, Shift+Enter
 * quebra linha). Marca como lido ao abrir (`useMarkRead`, uma vez por threadId).
 *
 * Marcadores por trecho (B2): cada mensagem carrega `anchorConversationId`. Como
 * threads 1:1 são deduplicados, um thread acumula mensagens ancoradas a conversas
 * DIFERENTES ao longo do tempo. Antes da 1ª mensagem de cada novo trecho ancorado
 * (anchor não-nulo E diferente da mensagem renderizada anterior) entra um divider
 * "◇ Sobre a Conversa #<id>" — botão que abre o peek (P13, dentro do popover). O
 * id é honesto (o peek não retorna nome do cliente). Substitui o antigo cartão
 * único de âncora a nível de thread.
 *
 * Reusa a camada de dados (`useThread`/`useReply`/`useMarkRead`) e os helpers
 * honestos de nome de `/mensagens` — não duplica nada. Bolha minha = `--bg-inverted`
 * ("casa", nunca âmbar); outros = `--bg-surface` (via classes mc-*).
 */

import { ChevronLeft, Link2, Lock, Send, Star } from 'lucide-react';
import { Fragment, useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';

import { useAuthStore } from '../../../../stores/auth-store';
import { relativeTime } from '../../../conversations/format';
import { useMarkRead, useReply, useThread } from '../../hooks/use-direct-messages';
import { useMarkReadOnIncoming } from '../../hooks/use-mark-read-on-incoming';
import {
  detailCounterpartName,
  groupParticipantsLabel,
  participantNameMap,
  senderDisplayName,
} from '../../thread-display';
import { usePinMutation, usePins } from '../hooks/use-pins';

import type { DirectMessage, ThreadDetail } from '../../types';

const MAX_HEIGHT_PX = 110;

interface CondensedThreadViewProps {
  threadId: string;
  onBack: () => void;
  onPeek: (conversationId: string) => void;
}

export function CondensedThreadView({
  threadId,
  onBack,
  onPeek,
}: CondensedThreadViewProps): JSX.Element {
  const { data: thread, isLoading, isError } = useThread(threadId);
  const markRead = useMarkRead();
  const reply = useReply(threadId);
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);

  // Marca como lido UMA vez ao abrir / trocar de thread (zera o badge). Ref evita
  // re-disparo por mudança de identidade do mutate.
  const mutateReadRef = useRef(markRead.mutate);
  mutateReadRef.current = markRead.mutate;
  useEffect(() => {
    if (threadId.length === 0) return;
    mutateReadRef.current(threadId);
  }, [threadId]);

  // N2-14: mensagem de OUTRO participante chegando com a thread aberta (e a aba
  // visível) também marca como lida — mesmo mecanismo da `ThreadView` de /mensagens.
  useMarkReadOnIncoming(threadId, thread, currentUserId, mutateReadRef);

  if (isLoading) {
    return (
      <div className="mc-view mc-view--flex">
        <div className="mc-subheader">
          <BackButton onBack={onBack} />
          <span className="mc-mono-meta" role="status">
            Abrindo conversa…
          </span>
        </div>
      </div>
    );
  }

  if (isError || thread === undefined) {
    return (
      <div className="mc-view mc-view--flex">
        <div className="mc-subheader">
          <BackButton onBack={onBack} />
          <span className="mc-subheader-plain">
            <Lock size={13} strokeWidth={1.6} aria-hidden /> Conversa indisponível
          </span>
        </div>
      </div>
    );
  }

  return (
    <ThreadLoaded
      thread={thread}
      onBack={onBack}
      onPeek={onPeek}
      onSend={(body) => {
        reply.mutate({ body });
      }}
      isSending={reply.isPending}
    />
  );
}

function BackButton({ onBack }: { onBack: () => void }): JSX.Element {
  return (
    <button type="button" className="mc-icon-btn" aria-label="Voltar" onClick={onBack}>
      <ChevronLeft size={15} strokeWidth={1.7} aria-hidden />
    </button>
  );
}

interface ThreadLoadedProps {
  thread: ThreadDetail;
  onBack: () => void;
  onPeek: (conversationId: string) => void;
  onSend: (body: string) => void;
  isSending: boolean;
}

function ThreadLoaded({
  thread,
  onBack,
  onPeek,
  onSend,
  isSending,
}: ThreadLoadedProps): JSX.Element {
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);
  const byUserId = participantNameMap(thread.participantNames);

  const { pins } = usePins();
  const { pin, unpin, isPending: isPinPending } = usePinMutation();
  const isPinned = pins.some((p) => p.targetType === 'thread' && p.targetId === thread.threadId);

  const isGroup = thread.kind === 'group';
  const title = isGroup
    ? groupParticipantsLabel(thread.participantUserIds.length)
    : detailCounterpartName(thread.participantUserIds, currentUserId, byUserId);

  // Auto-scroll para a mensagem mais nova ao carregar / após responder.
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const messageCount = thread.messages.length;
  useEffect(() => {
    const el = timelineRef.current;
    if (el !== null) el.scrollTop = el.scrollHeight;
  }, [messageCount]);

  const togglePin = (): void => {
    const body = { targetType: 'thread' as const, targetId: thread.threadId };
    if (isPinned) unpin(body);
    else pin(body);
  };

  return (
    <div className="mc-view mc-view--flex">
      <div className="mc-subheader">
        <BackButton onBack={onBack} />
        <span className="mc-subheader-title">
          <b>{title}</b>
        </span>
        <button
          type="button"
          className="mc-icon-btn"
          aria-pressed={isPinned}
          aria-label={isPinned ? 'Desafixar' : 'Fixar'}
          title={isPinned ? 'Desafixar' : 'Fixar'}
          disabled={isPinPending}
          onClick={togglePin}
        >
          <Star size={15} strokeWidth={1.3} className={isPinned ? 'mc-star-on' : ''} aria-hidden />
        </button>
      </div>

      <div ref={timelineRef} className="mc-timeline mc-scroll" aria-label="Mensagens">
        {thread.messages.length === 0 ? (
          <span className="mc-mono-meta">Nenhuma mensagem ainda. Escreva a primeira abaixo.</span>
        ) : (
          thread.messages.map((message, index) => {
            // Novo trecho: âncora não-nula E diferente da âncora da mensagem
            // RENDERIZADA anterior (null não fecha trecho — a ausência lê "geral").
            const prevAnchor =
              index === 0 ? null : (thread.messages[index - 1]?.anchorConversationId ?? null);
            const anchor = message.anchorConversationId;
            const stretchAnchor = anchor !== null && anchor !== prevAnchor ? anchor : null;
            return (
              <Fragment key={message.messageId}>
                {stretchAnchor !== null && (
                  <AnchorDivider anchorConversationId={stretchAnchor} onPeek={onPeek} />
                )}
                <Bubble
                  message={message}
                  isMine={message.senderUserId === currentUserId}
                  isGroup={isGroup}
                  byUserId={byUserId}
                />
              </Fragment>
            );
          })
        )}
      </div>

      <InlineComposer onSend={onSend} isSending={isSending} />
    </div>
  );
}

interface BubbleProps {
  message: DirectMessage;
  isMine: boolean;
  isGroup: boolean;
  byUserId: Map<string, string>;
}

function Bubble({ message, isMine, isGroup, byUserId }: BubbleProps): JSX.Element {
  const showSender = isGroup && !isMine;
  const senderName = senderDisplayName(message.senderUserId, byUserId);
  return (
    <div
      className={`mc-msg mc-msg--${isMine ? 'mine' : 'other'}`}
      data-mine={isMine ? 'true' : 'false'}
    >
      {showSender && <span className="mc-sender">{senderName}</span>}
      <div className="mc-bubble">{message.body}</div>
      <span className="mc-mono-meta">{relativeTime(message.createdAt)}</span>
    </div>
  );
}

/**
 * Marcador de início de trecho ancorado na timeline → porta para o peek (DENTRO do
 * popover, P13). Honesto: o peek não retorna nome do cliente, então mostra o id.
 */
function AnchorDivider({
  anchorConversationId,
  onPeek,
}: {
  anchorConversationId: string;
  onPeek: (conversationId: string) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className="mc-anchor-divider"
      aria-label={`Ver contexto da Conversa #${anchorConversationId}`}
      onClick={() => {
        onPeek(anchorConversationId);
      }}
    >
      <span className="mc-anchor-divider-inner">
        <Link2 size={11} strokeWidth={1.6} aria-hidden />
        <span className="mc-anchor-divider-label">
          Sobre a Conversa <span className="mc-anchor-divider-id">#{anchorConversationId}</span>
        </span>
      </span>
    </button>
  );
}

function InlineComposer({
  onSend,
  isSending,
}: {
  onSend: (body: string) => void;
  isSending: boolean;
}): JSX.Element {
  const [draft, setDraft] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const trimmed = draft.trim();
  const canSend = trimmed.length > 0 && !isSending;

  useEffect(() => {
    const el = textareaRef.current;
    if (el === null) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT_PX).toString()}px`;
  }, [draft]);

  const submit = useCallback(() => {
    const body = draft.trim();
    if (body.length === 0 || isSending) return;
    onSend(body);
    setDraft('');
  }, [draft, isSending, onSend]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter envia; Shift+Enter quebra linha (deixa o comportamento padrão).
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        submit();
      }
    },
    [submit],
  );

  return (
    <div className="mc-composer">
      <div className="mc-composer-field">
        <textarea
          ref={textareaRef}
          className="mc-textarea"
          value={draft}
          rows={1}
          placeholder="Responder…"
          aria-label="Responder à conversa interna"
          onChange={(e) => {
            setDraft(e.target.value);
          }}
          onKeyDown={onKeyDown}
        />
        <button
          type="button"
          className="mc-send-btn"
          aria-label="Enviar"
          title="Enviar (Enter)"
          disabled={!canSend}
          onClick={submit}
        >
          <Send size={14} strokeWidth={1.8} aria-hidden />
        </button>
      </div>
      <div className="mc-composer-hint">Enter envia · Shift+Enter quebra linha</div>
    </div>
  );
}
